import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Modal from 'core/elements/modal'
import Button from 'core/elements/button'
import ShowActionState, { ActionStateResult } from 'core/components/ShowActionState'
import useDeleteAction from 'core/hooks/useDeleteAction'
import Text from 'core/elements/Text'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Info from 'core/components/validatedForm/Info'
import { deleteVolume } from './actions'
import Grid from 'core/elements/grid/Grid'
import { ArrayElement } from 'core/actions/Action'
import { StatusCellComponent } from '../snapshots/VolumeSnapshotsListPage'
import SelectableCard from 'core/components/SelectableCard'
import Badge from 'core/elements/badge'
import { pageSizeOptions } from '../../vms/helpers'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { formatDate } from 'utils/misc'

interface DeleteVolumesDialogProps {
  rows: any[] // volumes
  onClose: (value: boolean) => void
  actionNames?: {
    inProgress?: string
    success?: string
    failed?: string
  }
  reloadVolumes?: () => void
}

type SelectorModel = ArrayElement<ReturnType<any>>

export default function DeleteVolumesDialog({
  rows,
  onClose,
  actionNames = {
    inProgress: 'Deleting',
    success: 'Deleted',
    failed: 'Delete Failed',
  },
  reloadVolumes,
}: DeleteVolumesDialogProps) {
  const classes = useStyles()
  const [isCascadeDelete, setIsCascadeDelete] = useState(false)
  const [isForceCascadeDelete, setIsForceCascadeDelete] = useState(false)

  const volumeDetails = (res) => {
    return (
      <>
        <Text variant="caption1">{res?.name}</Text>
        <div className={classes.volumeDetails}>
          <Text variant="body2">{res?.size || 0} GB </Text>
          <Text variant="body2">{res?.status}</Text>
          <Text variant="body2">{formatDate(res?.created_at)}</Text>
        </div>
      </>
    )
  }

  const [bulkResults, setBulkResults] = useState<Array<ActionStateResult>>(
    Array.from({ length: rows?.length }, (_, i) => ({
      name: volumeDetails(rows[i]),
      status: '' as const,
      actionName: '',
    })),
  )

  const { bulkRemove: bulkRemoveVolumes, deleting } = useDeleteAction(deleteVolume)

  const columns = [
    {
      key: 'snapshotName',
      label: 'Snapshot Name',
      render: (snapshotName) => <Text variant="caption1">{snapshotName}</Text>,
    },
    {
      key: 'size',
      label: 'Size (GB)',
      render: (size) => <Text variant="caption1">{size}</Text>,
    },
    {
      key: 'status',
      label: 'Snapshot Status',
      CellComponent: StatusCellComponent,
    },
    {
      key: 'createdAt',
      label: 'Created At',
      formatFn: (value) => formatDate(value),
    },
    ...(rows?.length > 1
      ? [
          {
            key: 'volumeName',
            label: 'Volume Name',
            render: (volumeName) => <Text variant="caption1">{volumeName}</Text>,
          },
        ]
      : []),
  ]

  const idToNameMap = useMemo(() => {
    return rows?.reduce((acc, row) => {
      if (row?.id) acc[row?.id] = row?.name || row?.id
      return acc
    }, {} as Record<string, string>)
  }, [rows])

  const attachedSnapshotsToSelectedVolumes = useMemo(
    () =>
      rows
        ?.filter((row) => row?.snapshots)
        .flatMap((row) =>
          row?.snapshots?.map((snapshot) => ({
            id: snapshot?.id,
            volumeName: row?.name,
            volumeId: row?.id,
            snapshotName: snapshot?.name,
            status: snapshot?.status,
            size: snapshot?.size,
            createdAt: snapshot?.created_at,
          })),
        ) || [],
    [rows],
  )

  const hasFailedDeletions = useMemo(
    () => bulkResults?.some((r) => r?.status === 'Failed' && r?.error?.includes('error_deleting')),
    [bulkResults],
  )

  const hasInUseVolumes = useMemo(() => rows?.some((row) => ['in-use'].includes(row?.status)), [
    rows,
  ])

  const recommendForceCascadeDelete = useMemo(() => {
    return (
      hasFailedDeletions ||
      rows?.some((row) => ['deleting', 'error_deleting', 'error'].includes(row?.status))
    )
  }, [rows, hasFailedDeletions])

  useEffect(() => {
    const hasAttachedSnapshots = attachedSnapshotsToSelectedVolumes?.length > 0
    const isAdminAndShouldForceCascade = recommendForceCascadeDelete && isAdmin()

    setIsCascadeDelete(hasAttachedSnapshots && !isAdminAndShouldForceCascade)
    setIsForceCascadeDelete(isAdminAndShouldForceCascade)
  }, [recommendForceCascadeDelete, attachedSnapshotsToSelectedVolumes])

  const handleDelete = useCallback(async () => {
    const initialResults: ActionStateResult[] = rows?.map((row) => ({
      name: row?.name || row?.id,
      status: 'In Progress',
      actionName: actionNames.inProgress ?? 'Deleting',
    }))
    setBulkResults(initialResults)

    const volumesWithForceOrCascade = rows?.map((row) => ({
      ...row,
      isForceCascade: isForceCascadeDelete,
      isCascade: isCascadeDelete,
    }))
    const results = await bulkRemoveVolumes(volumesWithForceOrCascade)

    const toActionStateResult = (res: any): ActionStateResult => ({
      name: volumeDetails(rows.find((r) => r?.id === res?.params?.id)),
      status: res?.success ? 'Success' : 'Failed',
      actionName: res?.success
        ? actionNames.success ?? 'Deleted'
        : actionNames.failed ?? 'Delete Failed',
      error: res?.success ? null : res?.error?.message,
    })
    setBulkResults(results?.map(toActionStateResult))
    reloadVolumes?.()
  }, [
    rows,
    bulkRemoveVolumes,
    idToNameMap,
    actionNames,
    isForceCascadeDelete,
    isCascadeDelete,
    reloadVolumes,
  ])

  const handleClose = useCallback(
    (success?: boolean) => {
      setBulkResults([])
      onClose(success)
    },
    [onClose],
  )

  const showFinishButton = useMemo(() => bulkResults?.some((r) => !!r?.status), [bulkResults])

  return (
    <Modal
      panel="dialog"
      open
      onClose={handleClose}
      title={'Delete Volume(s)'}
      maxWidth={800}
      footer={
        showFinishButton && !hasFailedDeletions ? (
          <Button variant="secondary" onClick={handleClose}>
            Finish
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleDelete} loading={deleting}>
              Confirm
            </Button>
          </>
        )
      }
    >
      <>
        <Text variant="subtitle2">{'You are about to delete the following volume(s):'}</Text>
        <ShowActionState customClassName={classes.bulkResults} title={''} results={bulkResults} />

        {hasFailedDeletions && !deleting && (
          <Info error className={classes.warningContainer}>
            <div className={classes.warning}>
              <Text variant="body2">
                <FontAwesomeIcon className={classes.icon}>triangle-exclamation</FontAwesomeIcon>
                <b>ERROR</b>
              </Text>
              <Text variant="body2">
                {rows.length === 1
                  ? `This volume is stuck in 'error_deleting' state. Normal deletion will likely fail. Force delete is recommended.`
                  : `Volumes are stuck in 'error_deleting' state. Normal deletion will likely fail. Force delete is recommended.`}
              </Text>
            </div>
          </Info>
        )}
        {(attachedSnapshotsToSelectedVolumes?.length > 0 || hasInUseVolumes) && (
          <Info warning className={classes.warningContainer}>
            <div className={classes.warning}>
              <Text variant="body2">
                <FontAwesomeIcon className={classes.icon}>triangle-exclamation</FontAwesomeIcon>
                <b>WARNING</b>
              </Text>
              {attachedSnapshotsToSelectedVolumes?.length > 0 && (
                <Text variant="body2">
                  {`${
                    rows.length === 1 ? 'This volume' : 'Volumes'
                  } cannot be deleted because one or more selected volumes have ${
                    attachedSnapshotsToSelectedVolumes?.length
                  } associated snapshots. Select an option below to proceed.`}
                </Text>
              )}
              {hasInUseVolumes && (
                <Text variant="body2">
                  {`One or more selected volumes are in use and cannot be deleted. Detach them before retrying.`}
                </Text>
              )}
            </div>
          </Info>
        )}
        {attachedSnapshotsToSelectedVolumes?.length > 0 && (
          <div className={classes.container}>
            <Grid<SelectorModel>
              // @ts-ignore
              uniqueIdentifier="id"
              columns={columns}
              data={attachedSnapshotsToSelectedVolumes}
              label="Associated Snapshots"
              orderBy="name"
              orderDirection="asc"
              emptyContent="No snapshots associated with this volume(s)."
              disableRowSelection={false}
              hideCheckboxes
              disableColumnHiding
              rowsPerPage={3}
              pageSizeOptions={pageSizeOptions}
            />
          </div>
        )}
        <div className={classes.cardContainer}>
          <Text variant="caption1">{'Deletion Option(s)'}</Text>

          {attachedSnapshotsToSelectedVolumes?.length > 0 && (
            <SelectableCard
              id={'cascadeDelete'}
              onClick={() => {
                setIsCascadeDelete(!isCascadeDelete)
                setIsForceCascadeDelete(false)
              }}
              active={isCascadeDelete}
              className={classes.card}
              showRadioButton
            >
              <div className={classes.cardTitle}>
                <Text variant="caption1">{'Cascade delete: Delete volume and all snapshots'}</Text>
                {!recommendForceCascadeDelete && <Badge variant="success" text="Recommended" />}
              </div>
              <Text variant="body2" color="textSecondary">
                {recommendForceCascadeDelete ? (
                  <Text variant="body2" className={classes.warningText}>
                    <FontAwesomeIcon className={(classes.icon, classes.warningText)}>
                      triangle-exclamation
                    </FontAwesomeIcon>{' '}
                    May fail due to volume error state
                  </Text>
                ) : (
                  <>{`Permanently deletes this volume and all associated snapshots. This action cannot be undone.`}</>
                )}
              </Text>
            </SelectableCard>
          )}

          <SelectableCard
            id={'forceCascadeDelete'}
            onClick={() => {
              setIsForceCascadeDelete(!isForceCascadeDelete)
              setIsCascadeDelete(false)
            }}
            active={isForceCascadeDelete}
            className={classes.card}
            showRadioButton
            disabled={!isAdmin()}
          >
            <div className={classes.cardTitle}>
              <Text variant="caption1">{'Force cascade delete'}</Text>
              {recommendForceCascadeDelete && <Badge variant="success" text="Recommended" />}{' '}
            </div>
            <Text variant="body2" color="textSecondary">
              {`Forcefully deletes this volume and associated snapshot, bypassing state checks. Attempts deletion even if the volume is in an error state.
              Requires administrator privileges.`}
            </Text>
          </SelectableCard>
        </div>
      </>
    </Modal>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  warningContainer: { marginTop: theme.spacing(2) },
  warning: { display: 'grid', gap: 8 },
  icon: { marginRight: theme.spacing(1) },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    padding: theme.spacing(1, 0, 0, 0),
  },
  checkbox: { cursor: 'pointer', padding: 0 },
  cardContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  card: {
    width: '100%',
    height: '100%',
    '& .card-body': {
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'start',
      maxWidth: 670,
      padding: theme.spacing(2, 2),
    },
    '& .checkbox': { marginTop: theme.spacing(0.5) },
  },
  cardTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  bulkResults: {
    gridTemplateColumns: '12px 400px max-content',
  },
  warningText: {
    color: theme.components.graph.warning,
  },
  volumeDetails: {
    display: 'grid',
    gridTemplateColumns: '50px 80px 170px',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
}))
